import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { formatAmount } from '@/lib/venue/format'
import { RESERVATION_STATUS_TONE, bookerNameText } from '@/lib/venue/reservationViewModel'
import type { VenueRevenueReservationItem } from '@/lib/venue/revenueTypes'
import { amountTextClass, revenueFeeCellText, revenueUseDateTimeText } from '@/lib/venue/revenueViewModel'

/**
 * 예약별 매출 내역 목록 — 컬럼 9종(이슈 요구사항 3) + "더 보기" 커서 페이지네이션.
 *
 * 🚨 Figma 웹 목업(`n41`)의 번호 매김 대신 저장소 기존 `ReservationListTable`과 같은 "더 보기" 버튼을
 * 쓴다(설계 §2 자율 결정 2) — 서버 커서(`nextCursor`)가 불투명 문자열이라 임의 페이지로 점프할 수 없다.
 * 🚫 조회 전용 화면이라 행 클릭으로 상세 이동을 만들지 않는다(이슈 요구사항 5).
 */
export function RevenueReservationListTable({
  items,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: {
  items: VenueRevenueReservationItem[]
  hasNextPage: boolean
  isFetchingNextPage: boolean
  onLoadMore: () => void
}) {
  if (items.length === 0) {
    return <EmptyState title="조회된 예약 매출 내역이 없습니다" />
  }

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-xl border border-slateish-200">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-slateish-200 bg-slateish-50 text-left text-xs font-medium text-slateish-600">
              <th className="px-3 py-2">예약 번호</th>
              <th className="px-3 py-2">예약 일시</th>
              <th className="px-3 py-2">코트</th>
              <th className="px-3 py-2">예약자</th>
              <th className="px-3 py-2">결제 금액</th>
              <th className="px-3 py-2">적용 수수료</th>
              <th className="px-3 py-2">환불 금액</th>
              <th className="px-3 py-2">정산 반영액</th>
              <th className="px-3 py-2">예약 상태</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.reservationId} className="border-b border-slateish-200 last:border-b-0">
                <td className="px-3 py-2">{item.reservationNo ?? '-'}</td>
                <td className="px-3 py-2">{revenueUseDateTimeText(item)}</td>
                <td className="px-3 py-2">{item.courtName}</td>
                <td className="px-3 py-2">{bookerNameText(item)}</td>
                <td className="px-3 py-2">{formatAmount(item.paymentAmount)}</td>
                <td className="px-3 py-2">{revenueFeeCellText(item)}</td>
                <td className="px-3 py-2">
                  {item.refundedAmount > 0
                    ? `-${formatAmount(item.refundedAmount)}`
                    : formatAmount(item.refundedAmount)}
                </td>
                <td className={`px-3 py-2 font-medium ${amountTextClass(item.settlementReflectedAmount)}`}>
                  {formatAmount(item.settlementReflectedAmount)}
                </td>
                <td className="px-3 py-2">
                  <Badge tone={RESERVATION_STATUS_TONE[item.status]}>{item.statusLabel}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hasNextPage ? (
        <div className="flex justify-center">
          <Button variant="secondary" size="sm" disabled={isFetchingNextPage} onClick={onLoadMore}>
            {isFetchingNextPage ? '불러오는 중' : '더 보기'}
          </Button>
        </div>
      ) : null}
    </div>
  )
}
